import { useState } from 'react';
import type { TrendItem } from '@/mocks/hotNews';
import { trendItems } from '@/mocks/hotNews';

const deltas = ['up', 'up', 'down', 'flat', 'new'] as const;

function randomHeat(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export default function Trending() {
  const [list, setList] = useState<TrendItem[]>(trendItems);

  const refresh = () => {
    setList((prev) =>
      prev
        .map((item) => ({
          ...item,
          heat: randomHeat(320, 998),
          delta: deltas[Math.floor(Math.random() * deltas.length)],
        }))
        .sort((a, b) => b.heat - a.heat)
        .map((item, idx) => ({ ...item, rank: idx + 1 })),
    );
  };

  const rankBadge = (rank: number) =>
    rank <= 3 ? 'text-primary-600' : 'text-foreground-300';

  return (
    <section id="trending" className="w-full scroll-mt-24 bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
          {/* ranked list */}
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
                  <i className="ri-fire-fill" aria-hidden="true" /> LIVE BOARD
                </p>
                <h2 className="mt-2 font-display text-2xl font-black md:text-3xl">全网热搜 · 现场围观榜</h2>
                <p className="mt-2 text-sm text-foreground-600">实时热度实时涨，随时刷新换一换口味，吃瓜也要有讲究。</p>
              </div>
            </div>

            <ul className="mt-6 space-y-2">
              {list.map((item) => (
                <li key={item.id} className="group flex items-center gap-4 rounded-xl bg-background-50 px-4 py-3 transition-colors hover:bg-background-200/70 md:px-5">
                  <span className={`w-7 shrink-0 font-display text-xl font-black ${rankBadge(item.rank)}`}>{item.rank}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">{item.category}</span>
                      <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-700 md:text-base">{item.title}</h3>
                    </div>
                  </div>
                  <span className="hidden shrink-0 text-xs text-foreground-500 sm:block">{item.heat.toLocaleString()}热度</span>
                  <span className={`shrink-0 flex h-6 w-6 items-center justify-center rounded-full ${item.delta === 'up' ? 'bg-primary-100 text-primary-700' : item.delta === 'down' ? 'bg-accent-100 text-accent-700' : item.delta === 'new' ? 'bg-secondary-200 text-secondary-900' : 'bg-background-200 text-foreground-500'}`}>
                    {item.delta === 'up' ? <i className="ri-arrow-up-line text-sm" /> : item.delta === 'down' ? <i className="ri-arrow-down-line text-sm" /> : item.delta === 'new' ? <i className="ri-sparkling-2-line text-sm" /> : <i className="ri-subtract-line text-sm" />}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* side panel */}
          <aside className="flex flex-col gap-4 lg:col-span-1">
            <div className="rounded-2xl bg-accent-500 p-6 text-background-50">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest opacity-90">
                <i className="ri-radar-line" aria-hidden="true" /> 瓜力值雷达
              </p>
              <p className="mt-4 font-display text-4xl font-black">
                {list.slice(0, 3).reduce((a, b) => a + b.heat, 0)}
                <span className="ml-2 text-sm font-normal opacity-90">今日三甲热度</span>
              </p>
              <p className="mt-2 text-sm leading-6 opacity-90">排名每分钟都在变化，榜首之争异常激烈，围观也要趁热。</p>
              <button type="button" onClick={refresh} className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-accent-700 transition-colors hover:bg-background-100">
                <i className="ri-refresh-line" aria-hidden="true" /> 立即刷新榜单
              </button>
            </div>
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-display text-sm font-bold">围观小贴士</h3>
              <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                <li className="flex gap-2"><span className="text-accent-600"><i className="ri-eye-line" /></span>热度仅代表讨论量，不代表事件对错。</li>
                <li className="flex gap-2"><span className="text-accent-600"><i className="ri-user-star-line" /></span>实况以多方信源交叉验证为准，吃瓜别急着站队。</li>
                <li className="flex gap-2"><span className="text-accent-600"><i className="ri-shield-check-line" /></span>理性围观，守住底线，拒绝造谣传谣。</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}